/**
 * Organizacje kynologiczne — enum + mapowanie.
 * Enum zgodny z backendem (pole: organization_code na entities).
 *
 * MVP filtra w UI: 4 opcje (Wszystkie / FCI / Inne / Bez rodowodu).
 * V2: rozbicie "Inne" na konkretne checkboxy.
 */

// Kanoniczne kody (zapisane w DB)
export const ORG_CODES = {
  FCI_ZKWP: 'fci_zkwp',
  PKPR: 'pkpr',
  CANIS_E_CATUS: 'canis_e_catus',
  KENNEL_CLUB: 'kennel_club',
  AKC: 'akc',
  FCI_FOREIGN: 'fci_foreign',
  OTHER_PL: 'other_pl',
  OTHER_FOREIGN: 'other_foreign',
  NO_PEDIGREE: 'no_pedigree',
  UNKNOWN: 'unknown',
};

// Label dla usera (co widzi)
export const ORG_LABELS = {
  [ORG_CODES.FCI_ZKWP]: 'FCI / ZKwP',
  [ORG_CODES.PKPR]: 'PKPR',
  [ORG_CODES.CANIS_E_CATUS]: 'Canis e Catus',
  [ORG_CODES.KENNEL_CLUB]: 'The Kennel Club (UK)',
  [ORG_CODES.AKC]: 'American Kennel Club (AKC)',
  [ORG_CODES.FCI_FOREIGN]: 'FCI (zagraniczne)',
  [ORG_CODES.OTHER_PL]: 'Inny polski związek',
  [ORG_CODES.OTHER_FOREIGN]: 'Inna zagraniczna organizacja',
  [ORG_CODES.NO_PEDIGREE]: 'Bez rodowodu',
  [ORG_CODES.UNKNOWN]: 'Nie podano',
};

// Tier dla sortowania (FCI pierwsze, inne niżej, unknown na końcu)
export const ORG_TIER = {
  [ORG_CODES.FCI_ZKWP]: 1,
  [ORG_CODES.PKPR]: 2,
  [ORG_CODES.CANIS_E_CATUS]: 2,
  [ORG_CODES.KENNEL_CLUB]: 2,
  [ORG_CODES.AKC]: 2,
  [ORG_CODES.FCI_FOREIGN]: 2,
  [ORG_CODES.OTHER_PL]: 2,
  [ORG_CODES.OTHER_FOREIGN]: 2,
  [ORG_CODES.NO_PEDIGREE]: 3,
  [ORG_CODES.UNKNOWN]: 4,
};

// MVP filtr UI → lista kodów backendu
export const ORG_FILTER_GROUPS = [
  { key: 'all', label: 'Wszystkie', codes: [] },
  {
    key: 'fci',
    label: 'FCI / ZKwP',
    codes: [ORG_CODES.FCI_ZKWP],
    color: 'emerald',
  },
  {
    key: 'other',
    label: 'Inne',
    codes: [
      ORG_CODES.PKPR,
      ORG_CODES.CANIS_E_CATUS,
      ORG_CODES.KENNEL_CLUB,
      ORG_CODES.AKC,
      ORG_CODES.FCI_FOREIGN,
      ORG_CODES.OTHER_PL,
      ORG_CODES.OTHER_FOREIGN,
    ],
    color: 'blue',
  },
  {
    key: 'no_pedigree',
    label: 'Bez rodowodu',
    codes: [ORG_CODES.NO_PEDIGREE],
    color: 'gray',
  },
];

/**
 * Zgadnij organization_code ze starszych pól (is_fci, organization_type, organization).
 * Używane do stopniowej migracji danych — docelowo wszystkie encje mają `organization_code`.
 */
export function deriveOrgCode(entity) {
  if (!entity) return ORG_CODES.UNKNOWN;
  if (entity.organization_code) return entity.organization_code;
  if (entity.is_fci === true) return ORG_CODES.FCI_ZKWP;
  const t = String(entity.organization_type || entity.organization || '').toLowerCase().trim();
  if (!t || t === 'unknown') return ORG_CODES.UNKNOWN;
  if (t.includes('fci') || t.includes('zkwp')) return ORG_CODES.FCI_ZKWP;
  if (t.includes('pkpr')) return ORG_CODES.PKPR;
  if (t.includes('canis') || t.includes('catus')) return ORG_CODES.CANIS_E_CATUS;
  if (t.includes('kennel club') || t === 'kc' || t === 'uk kc') return ORG_CODES.KENNEL_CLUB;
  if (t.includes('akc')) return ORG_CODES.AKC;
  if (t.includes('no_pedigree') || t.includes('bez rodowodu') || t === 'none') return ORG_CODES.NO_PEDIGREE;
  if (t.includes('other') || t.includes('inne') || t.includes('inna')) return ORG_CODES.OTHER_PL;
  return ORG_CODES.UNKNOWN;
}

export function getOrgLabel(code) {
  return ORG_LABELS[code] || 'Nie podano';
}

export function getOrgTier(code) {
  return ORG_TIER[code] ?? 4;
}
